
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Check, XCircle } from "lucide-react";
import { AgentExecutionResponse } from "@/models/agent-runtime";
import { format } from "date-fns";

interface AgentRuntimeHistoryProps {
  history: AgentExecutionResponse[];
  onSelectResult: (result: AgentExecutionResponse) => void;
}

export function AgentRuntimeHistory({ history, onSelectResult }: AgentRuntimeHistoryProps) {
  if (!history || history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Execution History</CardTitle>
          <CardDescription>View your past runs of this agent</CardDescription>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">No execution history available yet.</p>
          <p className="text-sm mt-2">Run the agent to see results appear here.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Execution History</CardTitle>
        <CardDescription>View your past runs of this agent</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Tokens</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {item.timestamp ? 
                    format(new Date(item.timestamp), 'MMM d, yyyy HH:mm') : 
                    'Unknown date'}
                </TableCell>
                <TableCell>
                  {item.success ? (
                    <span className="flex items-center text-green-600">
                      <Check className="h-4 w-4 mr-1" /> Success
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600">
                      <XCircle className="h-4 w-4 mr-1" /> Error
                    </span>
                  )}
                </TableCell>
                <TableCell>{item.executionTime ? `${(item.executionTime / 1000).toFixed(2)}s` : 'N/A'}</TableCell>
                <TableCell>
                  {item.tokenUsage ? 
                    `${item.tokenUsage.total || 0}` : 
                    'N/A'}
                </TableCell>
                <TableCell className="text-right">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onSelectResult(item)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
